import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, Plus, Minus, Volume2, VolumeX } from "lucide-react";
import { Metronome, Pad, Sequencer, playVoice, ac, unlockAudio, audioBlocked } from "@/lib/audio";
import { KEYS } from "@/lib/transpose";

/* Shows a tap-to-enable notice when the browser is blocking audio (autoplay / embedded preview). */
export function AudioNotice({ blocked, onEnable }) {
  if (!blocked) return null;
  return (
    <button onClick={onEnable} data-testid="audio-enable-notice"
      className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-200 text-sm font-medium">
      <VolumeX className="w-4 h-4" /> Sound is blocked by your browser — tap to enable audio
    </button>
  );
}

/* ============ METRONOME ============ */
const MET_SOUNDS = ["click", "wood", "electronic", "shimmer", "clap", "kick", "snare", "perc"];
const TIME_SIGS = [{ l: "4/4", b: 4 }, { l: "3/4", b: 3 }, { l: "6/8", b: 6 }, { l: "2/4", b: 2 }, { l: "5/4", b: 5 }];
const SUBS = [{ l: "Quarter", v: 1 }, { l: "8th", v: 2 }, { l: "16th", v: 4 }, { l: "Triplet", v: 3 }];

export function MetronomePanel({ initialBpm = 72, compact = false }) {
  const [bpm, setBpm] = useState(initialBpm);
  const [beats, setBeats] = useState(4);
  const [sub, setSub] = useState(1);
  const [sound, setSound] = useState("click");
  const [accent, setAccent] = useState(true);
  const [running, setRunning] = useState(false);
  const [beat, setBeat] = useState(-1);
  const [blocked, setBlocked] = useState(false);
  const metRef = useRef(null);
  const tapRef = useRef([]);

  useEffect(() => { metRef.current = new Metronome(); metRef.current.onBeat = (b) => setBeat(b); return () => metRef.current?.stop(); }, []);
  useEffect(() => { const m = metRef.current; if (!m) return; m.bpm = bpm; m.beats = beats; m.sub = sub; m.sound = sound; m.accent = accent; }, [bpm, beats, sub, sound, accent]);

  const toggle = async () => {
    const m = metRef.current;
    if (running) { m.stop(); setRunning(false); setBeat(-1); }
    else {
      await unlockAudio();
      setBlocked(audioBlocked());
      m.bpm = bpm; m.beats = beats; m.sub = sub; m.sound = sound; m.accent = accent; m.start(); setRunning(true);
    }
  };
  const enable = async () => { await unlockAudio(); setBlocked(audioBlocked()); };
  const tap = () => {
    const now = performance.now();
    tapRef.current = [...tapRef.current.filter((t) => now - t < 2500), now];
    if (tapRef.current.length >= 2) {
      const gaps = []; for (let i = 1; i < tapRef.current.length; i++) gaps.push(tapRef.current[i] - tapRef.current[i - 1]);
      const avg = gaps.reduce((a, b) => a + b, 0) / gaps.length;
      const v = Math.round(60000 / avg); if (v >= 30 && v <= 300) setBpm(v);
    }
  };

  return (
    <div className="bg-[#090D17] border border-[#E5C378]/25 rounded-2xl p-6 flex flex-col items-center text-center shadow-2xl">
      <AudioNotice blocked={blocked} onEnable={enable} />
      <div className="font-mono text-6xl sm:text-7xl font-bold tracking-tight text-white mb-1" data-testid="metronome-bpm">{bpm}</div>
      <div className="text-xs text-[#8A97AC] mb-4">BPM</div>
      <div className="flex gap-2 mb-5">
        {Array.from({ length: beats }).map((_, i) => (
          <div key={i} className={`w-6 h-6 rounded-full transition-all ${beat === i ? (i === 0 ? "bg-rose-500 shadow-[0_0_20px_#f43f5e] ring-2 ring-white" : "bg-[#E5C378] shadow-[0_0_16px_#E5C378]") : "bg-gray-800 border border-gray-700"}`} />
        ))}
      </div>
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => setBpm((b) => Math.max(30, b - 1))} className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] flex items-center justify-center"><Minus className="w-4 h-4" /></button>
        <input type="range" min="30" max="240" value={bpm} onChange={(e) => setBpm(Number(e.target.value))} className="w-40 accent-[#E5C378]" data-testid="metronome-slider" />
        <button onClick={() => setBpm((b) => Math.min(240, b + 1))} className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] flex items-center justify-center"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-2 mb-4">
        <button onClick={toggle} data-testid="btn-metronome-toggle" className="flex items-center gap-2 px-6 py-3 rounded-xl wf-gold-btn font-bold">
          {running ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />} {running ? "Stop" : "Start"}
        </button>
        <button onClick={tap} data-testid="btn-metronome-tap" className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] font-semibold">Tap</button>
      </div>
      {!compact && (
        <div className="grid grid-cols-2 gap-2 w-full text-left mt-2">
          <label className="text-xs text-[#8A97AC]">Time signature
            <select value={beats} onChange={(e) => setBeats(Number(e.target.value))} className="mt-1 w-full bg-[#0B101D] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-[#F7F5F0]">
              {TIME_SIGS.map((t) => <option key={t.l} value={t.b}>{t.l}</option>)}
            </select>
          </label>
          <label className="text-xs text-[#8A97AC]">Subdivision
            <select value={sub} onChange={(e) => setSub(Number(e.target.value))} className="mt-1 w-full bg-[#0B101D] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-[#F7F5F0]">
              {SUBS.map((t) => <option key={t.l} value={t.v}>{t.l}</option>)}
            </select>
          </label>
          <label className="text-xs text-[#8A97AC]">Sound
            <select value={sound} onChange={(e) => setSound(e.target.value)} className="mt-1 w-full bg-[#0B101D] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-[#F7F5F0] capitalize">
              {MET_SOUNDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-[#8A97AC] mt-5">
            <input type="checkbox" checked={accent} onChange={(e) => setAccent(e.target.checked)} className="accent-[#E5C378]" /> Accent beat 1
          </label>
        </div>
      )}
    </div>
  );
}

/* ============ PAD PLAYER ============ */
const PAD_CATS = ["Warm", "Atmospheric", "Cinematic", "Prayer", "Ambient", "Dark", "Bright"];
const PAD_KEYS = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];

export function PadPanel({ defaultKey = "E" }) {
  const [active, setActive] = useState(null);
  const [cat, setCat] = useState("Warm");
  const [vol, setVol] = useState(0.5);
  const [blocked, setBlocked] = useState(false);
  const padRef = useRef(null);
  useEffect(() => { padRef.current = new Pad(); return () => padRef.current?.stop(0.3); }, []);
  const play = async (key) => {
    await unlockAudio();
    setBlocked(audioBlocked());
    const p = padRef.current; p.setVolume(vol);
    if (active === key) { p.stop(2); setActive(null); }
    else { p.crossfade(key, cat, active ? 3 : 2.2); setActive(key); }
  };
  const enable = async () => { await unlockAudio(); setBlocked(audioBlocked()); };
  useEffect(() => { if (padRef.current) padRef.current.setVolume(vol); }, [vol]);
  useEffect(() => { if (active && padRef.current) padRef.current.crossfade(active, cat, 3); /* eslint-disable-next-line */ }, [cat]);

  return (
    <div className="bg-gradient-to-b from-[#0F1626] to-[#0A0D17] border border-[#4B7BE5]/30 rounded-2xl p-6">
      <AudioNotice blocked={blocked} onEnable={enable} />
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="eyebrow">Worship atmosphere</div>
          <h3 className="font-display font-semibold text-[#F7F5F0] text-lg">Ambient Pads</h3>
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="pad-category" className="bg-[#0B101D] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F7F5F0]">
          {PAD_CATS.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2.5 mb-4">
        {PAD_KEYS.map((k) => (
          <button key={k} onClick={() => play(k)} data-testid={`pad-key-${k}`}
            className={`h-14 rounded-xl font-mono font-bold flex items-center justify-center transition-all ${active === k ? "bg-[#E5C378] text-[#090D16] border-2 border-white shadow-[0_0_25px_rgba(229,195,120,0.4)] scale-105 text-xl" : "bg-[#141E33] text-gray-200 border border-white/10 hover:border-[#E5C378]/50 text-lg"}`}>
            {k}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-[#8A97AC]">Volume</span>
        <input type="range" min="0" max="1" step="0.01" value={vol} onChange={(e) => setVol(Number(e.target.value))} className="flex-1 accent-[#E5C378]" data-testid="pad-volume" />
        {active && <button onClick={() => { padRef.current.stop(2); setActive(null); }} className="text-xs text-rose-400 hover:underline">Fade out</button>}
      </div>
      {active && <div className="mt-3 text-center text-sm text-[#E5C378] flex items-center justify-center gap-2"><span className="w-2 h-2 rounded-full bg-[#E5C378] wf-live-dot" /> Playing {active} · {cat}</div>}
    </div>
  );
}

/* ============ STEP SEQUENCER (drums / shaker) ============ */
const DRUM_ROWS = [
  { ch: "crash", label: "Crash" }, { ch: "openhat", label: "Open Hat" }, { ch: "hat", label: "Hi-Hat" },
  { ch: "clap", label: "Clap" }, { ch: "snare", label: "Snare" }, { ch: "tom", label: "Tom" }, { ch: "kick", label: "Kick" },
];
const SHAKER_ROWS = [
  { ch: "tambourine", label: "Tambourine" }, { ch: "shaker", label: "Shaker" },
  { ch: "clave", label: "Clave" }, { ch: "stick", label: "Stick" }, { ch: "conga", label: "Conga" },
];

const DRUM_PRESETS = {
  "Basic Worship": { kick: [0, 8], snare: [4, 12], hat: [0, 2, 4, 6, 8, 10, 12, 14] },
  "Driving Worship": { kick: [0, 6, 8, 14], snare: [4, 12], hat: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] },
  "Half Time": { kick: [0], snare: [8], hat: [0, 4, 8, 12] },
  "4-on-floor": { kick: [0, 4, 8, 12], snare: [4, 12], hat: [2, 6, 10, 14], clap: [4, 12] },
  "Build": { kick: [0, 2, 4, 6, 8, 10, 12, 14], snare: [12, 14], crash: [0] },
};

function buildPattern(map, steps) {
  const p = {};
  Object.entries(map).forEach(([ch, arr]) => { p[ch] = Array.from({ length: steps }, (_, i) => arr.includes(i)); });
  return p;
}

export function SequencerPanel({ variant = "drums", initialBpm = 90 }) {
  const rows = variant === "drums" ? DRUM_ROWS : SHAKER_ROWS;
  const steps = 16;
  const [bpm, setBpm] = useState(initialBpm);
  const [swing, setSwing] = useState(0);
  const [running, setRunning] = useState(false);
  const [cur, setCur] = useState(-1);
  const [blocked, setBlocked] = useState(false);
  const [pattern, setPattern] = useState(() =>
    variant === "drums" ? buildPattern(DRUM_PRESETS["Basic Worship"], steps)
      : buildPattern({ shaker: [2, 6, 10, 14], tambourine: [0, 8] }, steps));
  const seqRef = useRef(null);

  useEffect(() => {
    seqRef.current = new Sequencer(steps);
    seqRef.current.onStep = (s) => setCur(s);
    return () => seqRef.current?.stop();
  }, []);
  useEffect(() => { const s = seqRef.current; if (s) { s.bpm = bpm; s.swing = swing; s.pattern = pattern; } }, [bpm, swing, pattern]);

  const toggle = async () => {
    const s = seqRef.current;
    if (running) { s.stop(); setRunning(false); setCur(-1); }
    else { await unlockAudio(); setBlocked(audioBlocked()); s.bpm = bpm; s.swing = swing; s.pattern = pattern; s.start(); setRunning(true); }
  };
  const enable = async () => { await unlockAudio(); setBlocked(audioBlocked()); };
  const toggleCell = (ch, i) => {
    setPattern((p) => { const row = [...(p[ch] || Array(steps).fill(false))]; row[i] = !row[i]; if (!running) { unlockAudio(); playVoice(ch); } return { ...p, [ch]: row }; });
  };
  const applyPreset = (name) => setPattern(buildPattern(DRUM_PRESETS[name], steps));

  return (
    <div className="bg-[#080B14] border border-white/10 rounded-2xl p-5 shadow-2xl">
      <AudioNotice blocked={blocked} onEnable={enable} />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button onClick={toggle} data-testid={`seq-${variant}-toggle`} className="flex items-center gap-2 px-5 py-2.5 rounded-xl wf-gold-btn font-bold">
          {running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {running ? "Stop" : "Play"}
        </button>
        <label className="text-xs text-[#8A97AC] flex items-center gap-2">BPM
          <input type="range" min="50" max="180" value={bpm} onChange={(e) => setBpm(Number(e.target.value))} className="accent-[#E5C378]" />
          <span className="font-mono text-[#F7F5F0] w-8">{bpm}</span>
        </label>
        <label className="text-xs text-[#8A97AC] flex items-center gap-2">Swing
          <input type="range" min="0" max="1" step="0.05" value={swing} onChange={(e) => setSwing(Number(e.target.value))} className="accent-[#E5C378]" />
        </label>
        {variant === "drums" && (
          <select onChange={(e) => e.target.value && applyPreset(e.target.value)} defaultValue="" className="bg-[#0B101D] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-[#F7F5F0]" data-testid="drum-preset">
            <option value="">Presets…</option>{Object.keys(DRUM_PRESETS).map((p) => <option key={p}>{p}</option>)}
          </select>
        )}
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[560px] space-y-1.5">
          {rows.map((r) => (
            <div key={r.ch} className="flex items-center gap-2">
              <div className="w-20 text-xs text-[#B8C1D1] text-right pr-1 shrink-0">{r.label}</div>
              <div className="flex gap-1">
                {Array.from({ length: steps }).map((_, i) => {
                  const on = pattern[r.ch]?.[i];
                  const beatStart = i % 4 === 0;
                  return (
                    <button key={i} onClick={() => toggleCell(r.ch, i)} data-testid={`cell-${r.ch}-${i}`}
                      className={`w-6 h-6 sm:w-7 sm:h-7 rounded-md transition-all ${on ? "bg-[#E5C378]" : beatStart ? "bg-white/10" : "bg-white/[0.04]"} ${cur === i ? "ring-2 ring-[#4E97FF]" : ""}`} />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
